import { useState, useCallback, useRef } from "react";
import { useUISlice } from "./useUISlice";
import { largeFileUploader } from "@/clients/large-file.plugin";
import { logBrowserCrash } from "@/telemetry";
import { actions, uploads } from "@/controller";
import { createAbortAwareActionError, createActionError } from "@/client-error";

const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY = 1000; // 1 second
const MAX_FILE_SIZE = 5 * 1024 * 1024 * 1024; // 5GB
const MULTIPART_UPLOAD_THRESHOLD = 32 * 1024 * 1024;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const toUploadError = (error: unknown, fallbackMessage: string) => {
  if (error instanceof Error) {
    return error;
  }

  if (typeof error === "string" && error.trim()) {
    return new Error(error);
  }

  return new Error(fallbackMessage);
};

const withRetry = async <T,>(
  operation: () => Promise<T>,
  retryCount = 0
): Promise<T> => {
  try {
    return await operation();
  } catch (error) {
    const normalizedError = toUploadError(error, "Upload failed");
    if (normalizedError.name === "AbortError") {
      throw normalizedError;
    }

    if (retryCount >= MAX_RETRIES) {
      throw normalizedError;
    }

    const delay = INITIAL_RETRY_DELAY * Math.pow(2, retryCount);
    await sleep(delay);
    return withRetry(operation, retryCount + 1);
  }
};

export const useUpload = () => {
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const suppressAbortErrorRef = useRef(false);
  // Set by cancelUpload; checked before each retry attempt starts a new xhr.
  // Without it, cancelling during withRetry's backoff sleep did nothing —
  // the retry created a fresh (unabortable) xhr and uploaded a file the user
  // had already cancelled, then created a transcript for it.
  const cancelledRef = useRef(false);

  const cancelUpload = useCallback((options?: { silent?: boolean }) => {
    suppressAbortErrorRef.current = options?.silent === true;
    cancelledRef.current = true;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    if (useUISlice.getState().alertLargeFileUpload) {
      void largeFileUploader.cancel().catch((error) => {
        console.error("Failed to cancel native large-file upload:", error);
      });
      useUISlice.getState().setAlertLargeFileUpload(undefined);
    }

    setIsUploading(false);
    setUploadProgress(0);
  }, []);

  const getUploadedFileUrl = async (params: {
    file: File;
    onSuccess?: (downloadedFileUrl: string) => Promise<void>;
    onError?: (error: Error) => void;
  }): Promise<void> => {
    if (params.file.size && params.file.size > MAX_FILE_SIZE) {
      params.onError?.(
        new Error(`File size exceeds ${MAX_FILE_SIZE / 1024 / 1024 / 1024}GB`)
      );
      return;
    }

    if (params.file && "path" in params.file) {
      useUISlice.getState().setAlertLargeFileUpload({
        fileName: params.file.name,
        filePath: params.file.path as string,
        onComplete: async (downloadedFileUrl) => {
          try {
            await params.onSuccess?.(downloadedFileUrl);
          } catch (error) {
            params.onError?.(
              toUploadError(error, "Upload failed. Please try again.")
            );
          }
        },
        onCancel: () => params.onError?.(createActionError("cancelled")),
        onError: params.onError,
      });

      return;
    }

    if (params.file.size > MULTIPART_UPLOAD_THRESHOLD) {
      let session: { objectKey: string; uploadId: string } | undefined;
      try {
        suppressAbortErrorRef.current = false;
        cancelledRef.current = false;
        abortControllerRef.current = new AbortController();
        setIsUploading(true);

        const created = await actions.transcript.createMultipartUpload(
          params.file.name,
          params.file.size,
          { signal: abortControllerRef.current.signal }
        );
        if ("error" in created) throw createAbortAwareActionError(created.error);
        session = created.value;

        for (let partNumber = 1; partNumber <= created.value.partCount; partNumber += 1) {
          const start = (partNumber - 1) * created.value.partSize;
          const end = Math.min(start + created.value.partSize, params.file.size);
          const part = params.file.slice(start, end);

          await withRetry(async () => {
            if (cancelledRef.current) {
              const error = new Error("Upload aborted");
              error.name = "AbortError";
              throw error;
            }

            const signed = await actions.transcript.getMultipartUploadPartUrl(
              {
                objectKey: created.value.objectKey,
                uploadId: created.value.uploadId,
                partNumber,
                fileSize: params.file.size,
              },
              { signal: abortControllerRef.current?.signal }
            );
            if ("error" in signed) throw createAbortAwareActionError(signed.error);

            const uploaded = await uploads.upload({
              url: signed.value.uploadUrl,
              body: part,
              contentType: params.file.type || "application/octet-stream",
              signal: abortControllerRef.current?.signal,
              onProgress: (partProgress) =>
                setUploadProgress(
                  Math.round(
                    ((start + (part.size * partProgress) / 100) /
                      params.file.size) *
                      100
                  )
                ),
            });
            if ("error" in uploaded) {
              const error = createActionError(uploaded.error);
              if (uploaded.error === "cancelled") error.name = "AbortError";
              throw error;
            }
          });
        }

        const completed = await withRetry(async () => {
          if (cancelledRef.current) {
            const error = new Error("Upload aborted");
            error.name = "AbortError";
            throw error;
          }
          const result = await actions.transcript.completeMultipartUpload(
            {
              objectKey: created.value.objectKey,
              uploadId: created.value.uploadId,
              fileSize: params.file.size,
            },
            { signal: abortControllerRef.current?.signal }
          );
          if ("error" in result) throw createAbortAwareActionError(result.error);
          return result.value;
        });

        session = undefined;
        abortControllerRef.current = null;
        setIsUploading(false);
        setUploadProgress(0);
        await params.onSuccess?.(completed.downloadUrl);
      } catch (error) {
        if (session) {
          await actions.transcript.abortMultipartUpload(session).catch(() => undefined);
        }
        const isAbortError = error instanceof Error && error.name === "AbortError";
        const shouldSuppressAbortError = isAbortError && suppressAbortErrorRef.current;
        abortControllerRef.current = null;
        suppressAbortErrorRef.current = false;
        setIsUploading(false);
        setUploadProgress(0);

        if (isAbortError) {
          if (!shouldSuppressAbortError) params.onError?.(createActionError("cancelled"));
        } else {
          logBrowserCrash("upload.multipart", error, { operation: "upload" });
          params.onError?.(toUploadError(error, "Upload failed. Please try again."));
        }
      }
      return;
    }

    try {
      // 1. Get upload URL
      const uploadDataResponse =
        await actions.transcript.getUploadData(params.file.name);

      if (
        "error" in uploadDataResponse ||
        !uploadDataResponse.value.uploadUrl ||
        !uploadDataResponse.value.downloadUrl
      ) {
        params.onError?.(
          "error" in uploadDataResponse
            ? createActionError(uploadDataResponse.error)
            : createActionError("invalidResponse"),
        );
        return;
      }

      const { uploadUrl, downloadUrl } = uploadDataResponse.value;

      // 2. Create abort controller for cancellation
      suppressAbortErrorRef.current = false;
      cancelledRef.current = false;
      abortControllerRef.current = new AbortController();

      // 3. Upload file to presigned URL
      setIsUploading(true);

      console.info("Uploading file to presigned URL", {
        fileSizeBytes: params.file.size,
        fileType: params.file.type,
      });

      await withRetry(async () => {
        // Cancelled during the backoff sleep before this attempt — bail
        // instead of firing a new, unabortable xhr.
        if (cancelledRef.current) {
          const error = new Error("Upload aborted");
          error.name = "AbortError";
          throw error;
        }
        const result = await uploads.upload({
          url: uploadUrl,
          body: params.file,
          contentType: params.file.type || "application/octet-stream",
          signal: abortControllerRef.current?.signal,
          onProgress: setUploadProgress,
        });
        if ("error" in result) {
          const error = createActionError(result.error);
          if (result.error === "cancelled") error.name = "AbortError";
          throw error;
        }
      });

      // 4. Upload successful
      abortControllerRef.current = null;
      suppressAbortErrorRef.current = false;
      setIsUploading(false);
      setUploadProgress(0);

      console.info("File uploaded successfully to presigned URL", {
        fileSizeBytes: params.file.size,
      });

      // 5. Call success callback with download URL
      await params.onSuccess?.(downloadUrl);
    } catch (error) {
      const isAbortError = error instanceof Error && error.name === "AbortError";
      const shouldSuppressAbortError =
        isAbortError && suppressAbortErrorRef.current;

      abortControllerRef.current = null;
      suppressAbortErrorRef.current = false;
      setIsUploading(false);
      setUploadProgress(0);

      if (isAbortError) {
        console.info("Upload cancelled by user");
        if (!shouldSuppressAbortError) {
          params.onError?.(createActionError("cancelled"));
        }
      } else {
        logBrowserCrash("upload.presigned", error, {
          operation: "upload",
        });
        params.onError?.(toUploadError(error, "Upload failed. Please try again."));
      }
    }
  };

  return {
    uploadProgress,
    isUploading,
    cancelUpload,
    getUploadedFileUrl,
  };
};
